import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import {
  ComponentGroupMeta,
  ServiceGroupView,
  ComponentView,
  HistoryCellTip,
  ServiceRowView,
  ServiceState,
  StatusHistoryDay,
  StatusHistoryResponse,
  StatusIncident,
  StatusResponse,
  StatusServiceRole,
} from '../../core/domain/status.model';
import { StatusApiService } from '../../core/services/status-api.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { TooltipDirective } from '../../core/directives/tooltip.directive';
import { NgTemplateOutlet, DecimalPipe } from '@angular/common';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { GlobalAlertComponent } from '../../core/components/global-alert/global-alert.component';
import { TranslatePipe } from '@ngx-translate/core';
import { checkedAgo } from './status-checked-ago.util';
import { HISTORY_DAYS, cardHistory, combineHistory, cutWindow, resolveDayCount } from './status-history.util';

/**
 * One card per product rather than per infrastructure layer, mirroring the cards of the API
 * (Portal.Core StatusComponentCatalog). Only the look lives here: which services make up a card, and
 * how they weigh, comes from the API's response.
 */
const GROUPS: ComponentGroupMeta[] = [
  { key: 'GastroManagement', icon: 'storefront-outline', iconSrc: 'assets/icon/portal-mark.png', labelKey: 'status.group.gastroManagement', fallbackServiceKeys: ['PortalApi', 'Database', 'ServiceBus', 'ServiceBusFunctions', 'TimedFunctions', 'SignalR'] },
  { key: 'TableStack', icon: 'calendar-outline', iconSrc: 'assets/icon/tablestack-mark.png', labelKey: 'status.group.tableStack', fallbackServiceKeys: ['PortalApi', 'Database', 'ServiceBus', 'ServiceBusFunctions'] },
  { key: 'MenuLink', icon: 'qr-code-outline', iconSrc: 'assets/icon/menulink-mark.png', labelKey: 'status.group.menuLink', fallbackServiceKeys: ['PublicApi', 'Database', 'SignalR'] },
  { key: 'DeveloperPortal', icon: 'code-slash-outline', labelKey: 'status.group.developerPortal', fallbackServiceKeys: ['DevFunctions', 'Database', 'PortalApi'] },
  { key: 'GmGuardian', icon: 'shield-checkmark-outline', labelKey: 'status.group.gmGuardian', fallbackServiceKeys: ['PortalApi', 'Database'] },
  { key: 'Chroniq', icon: 'time-outline', iconSrc: 'assets/icon/chroniq-mark.png', labelKey: 'status.group.chroniq', fallbackServiceKeys: ['ChroniqApi', 'ChroniqTerminals'] },
  { key: 'ScreenDeck', icon: 'tv-outline', iconSrc: 'assets/icon/screendeck-mark.png', labelKey: 'status.group.screenDeck', fallbackServiceKeys: ['ScreenDeckPublicApi', 'ScreenDeckApi', 'ScreenshotWorker', 'VideoCutter'] },
];

/** Human-facing name/icon for each internal ServiceKey, shown as a nested row under its product card(s). */
const SERVICE_META: Record<string, { icon: string; labelKey: string }> = {
  PortalApi: { icon: 'cloud-outline', labelKey: 'status.service.portalApi' },
  PublicApi: { icon: 'globe-outline', labelKey: 'status.service.publicApi' },
  Database: { icon: 'server-outline', labelKey: 'status.service.database' },
  ServiceBus: { icon: 'swap-horizontal-outline', labelKey: 'status.service.serviceBus' },
  ServiceBusFunctions: { icon: 'flash-outline', labelKey: 'status.service.serviceBusFunctions' },
  TimedFunctions: { icon: 'time-outline', labelKey: 'status.service.timedFunctions' },
  SignalR: { icon: 'radio-outline', labelKey: 'status.service.signalR' },
  DevFunctions: { icon: 'terminal-outline', labelKey: 'status.service.devFunctions' },
  ChroniqApi: { icon: 'cloud-outline', labelKey: 'status.service.chroniqApi' },
  ScreenDeckApi: { icon: 'cloud-outline', labelKey: 'status.service.screenDeckApi' },
  ScreenDeckPublicApi: { icon: 'globe-outline', labelKey: 'status.service.screenDeckPublicApi' },
  ScreenshotWorker: { icon: 'image-outline', labelKey: 'status.service.screenshotWorker' },
  VideoCutter: { icon: 'videocam-outline', labelKey: 'status.service.videoCutter' },
  ChroniqTerminals: { icon: 'hardware-chip-outline', labelKey: 'status.service.chroniqTerminals' },
};

/**
 * Every ProductKey name (Portal.Core/Domain/Enums/ProductKey.cs) the backend may put into an
 * incident's `affectedGroups`. That list is not the same as GROUPS: it comes from the incident's
 * own AffectedProducts column rather than from the cards, so it may also carry products that
 * have no monitored component card of their own.
 */
const PRODUCT_KEYS = new Set([
  'GastroManagement', 'TableStack', 'MenuLink', 'ScreenDeck', 'Chroniq', 'DeveloperPortal', 'GmGuardian',
]);

/** A card's rows are listed core first. */
const ROLE_ORDER: StatusServiceRole[] = ['Core', 'Supporting', 'Auxiliary'];

/** The states the legend explains, best to worst. Maintenance has its own section on the page. */
const LEGEND_STATES: ServiceState[] = ['Operational', 'Degraded', 'PartialOutage', 'MajorOutage', 'Unknown'];

@Component({
    selector: 'app-status',
    templateUrl: './status.page.html',
    styleUrls: ['./status.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        PortalNavbarComponent,
        IonicModule,
        TooltipDirective,
        NgTemplateOutlet,
        PortalFooterComponent,
        GlobalAlertComponent,
        DecimalPipe,
        TranslatePipe,
    ],
})
export class StatusPage implements OnInit, OnDestroy {
  /** True until the status answered. The history loads on its own, see `historyLoading`. */
  isLoading = true;
  loadError = false;

  historyLoading = true;
  /** The history failed while the status did not: the cards stay, only the bars stay empty. */
  historyError = false;

  /** Number of days the history bars currently render, which also drives the "vor N Tagen" footer label. */
  visibleDayCount = resolveDayCount(window.innerWidth);

  /** Drives the header variant: app-portal-navbar while signed out; signed in, the
   *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
  isAuthenticated = false;

  readonly legendStates = LEGEND_STATES;

  overallState: ServiceState = 'Unknown';
  generatedAtUtc: string | null = null;
  components: ComponentView[] = [];
  activeIncidents: StatusIncident[] = [];
  recentlyResolvedIncidents: StatusIncident[] = [];
  scheduledMaintenance: StatusIncident[] = [];
  /** Windows that have finished, kept behind a toggle so they don't read as still upcoming. */
  pastMaintenance: StatusIncident[] = [];
  showPastMaintenance = false;

  private status: StatusResponse | null = null;
  private history: StatusHistoryResponse | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  constructor(
    private statusApi: StatusApiService,
    private seo: SeoService,
    private cdr: ChangeDetectorRef,
    private readonly portalAuth: PortalAuthService,
  ) { }

  ngOnInit(): void {
    this.seo.apply({ titleKey: 'status.seoTitle', descriptionKey: 'status.heroSub', path: '/status' });
    this.visibleDayCount = resolveDayCount(window.innerWidth);
    this.load();

    this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
      this.isAuthenticated = auth;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refresh(): void {
    this.load(true);
  }

  /** Re-cuts the history window on rotation/resize; a no-op unless the width crosses a breakpoint. */
  @HostListener('window:resize')
  onResize(): void {
    const next = resolveDayCount(window.innerWidth);
    if (next === this.visibleDayCount) return;
    this.visibleDayCount = next;
    this.applyDayWindow();
    this.cdr.markForCheck();
  }

  roleLabelKey(role: StatusServiceRole): string {
    return `status.role.${role.charAt(0).toLowerCase()}${role.slice(1)}`;
  }

  roleHintKey(role: StatusServiceRole): string {
    return `status.roleHint.${role.charAt(0).toLowerCase()}${role.slice(1)}`;
  }

  /** The newest update of an incident, what a card shows in short. Updates arrive oldest first. */
  latestUpdate(incident: StatusIncident) {
    return incident.updates[incident.updates.length - 1] ?? null;
  }

  stateLabelKey(state: ServiceState): string {
    return `status.state.${state.charAt(0).toLowerCase()}${state.slice(1)}`;
  }

  legendLabelKey(state: ServiceState): string {
    return `status.legend.${state.charAt(0).toLowerCase()}${state.slice(1)}`;
  }

  /** "PartialOutage" → "partial-outage", for the state-colored CSS class. */
  stateClass(state: string): string {
    return state.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  }

  impactLabelKey(impact: string): string {
    return `status.impact.${impact.charAt(0).toLowerCase()}${impact.slice(1)}`;
  }

  stageLabelKey(stage: string): string {
    return `status.stage.${stage.charAt(0).toLowerCase()}${stage.slice(1)}`;
  }

  /**
   * Label for one entry of an incident's `affectedGroups`, the products an incident was tagged
   * with, shown as badges under its title. AffectedProducts is a free-form comma-separated
   * NVARCHAR that the API passes through without validating against ProductKey, so an
   * unrecognised value is returned verbatim: TranslateModule echoes back any key it has no
   * entry for, and echoing "ScreenDeck" beats echoing "status.group.screenDeckX".
   */
  productLabelKey(group: string): string {
    return PRODUCT_KEYS.has(group)
      ? `status.group.${group.charAt(0).toLowerCase()}${group.slice(1)}`
      : group;
  }

  /** Day-only label for the "monitored since" footer, the history bar has no finer resolution. */
  formatDate(isoDate: string): string {
    return new Date(isoDate + 'T00:00:00Z').toLocaleDateString('de-CH', {
      day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
    });
  }

  formatTimestamp(iso: string): string {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC',
    }) + ' UTC';
  }

  toggleExpanded(component: ComponentView): void {
    component.expanded = !component.expanded;
  }

  togglePastMaintenance(): void {
    this.showPastMaintenance = !this.showPastMaintenance;
  }

  stateIcon(state: ServiceState): string {
    switch (state) {
      case 'Operational': return 'checkmark-circle';
      case 'Degraded': return 'alert-circle';
      case 'PartialOutage': return 'alert-circle';
      case 'MajorOutage': return 'close-circle';
      case 'Maintenance': return 'construct';
      default: return 'help-circle';
    }
  }

  /** Rich tooltip content for one history-bar cell: date, state and exact uptime, or a no-data note. */
  historyCellTip(day: StatusHistoryDay | null): HistoryCellTip {
    if (!day) {
      return { dateLabel: '', stateLabelKey: '', uptimeLabel: null, hasData: false };
    }
    return {
      dateLabel: new Date(day.date + 'T00:00:00Z').toLocaleDateString('de-CH', {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC',
      }),
      stateLabelKey: this.stateLabelKey(day.worstState),
      uptimeLabel: day.uptimePercent.toFixed(2),
      hasData: true,
    };
  }

  /**
   * The two answers are independent: the cards render as soon as the status is there, and the bars fill
   * in when the history follows. A failing or slow history used to blank the whole page, although the
   * status it was waiting next to had long arrived.
   */
  private load(fresh = false): void {
    this.reload$.next();
    this.isLoading = true;
    this.loadError = false;
    this.historyLoading = true;
    this.historyError = false;

    this.statusApi.getStatus(fresh).pipe(takeUntil(this.reload$), takeUntil(this.destroy$)).subscribe({
      next: status => {
        this.status = status;
        this.isLoading = false;
        this.rebuild();
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });

    this.statusApi.getHistory(HISTORY_DAYS, fresh).pipe(takeUntil(this.reload$), takeUntil(this.destroy$)).subscribe({
      next: history => {
        this.history = history;
        this.historyLoading = false;
        this.rebuild();
      },
      error: () => {
        this.history = null;
        this.historyError = true;
        this.historyLoading = false;
        this.rebuild();
      },
    });
  }

  /** Builds the cards from whatever has arrived so far: the status, and the history if it is there yet. */
  private rebuild(): void {
    const status = this.status;
    if (!status) {
      this.cdr.markForCheck();
      return;
    }

    this.overallState = status.overallState;
    this.generatedAtUtc = status.generatedAtUtc;
    this.activeIncidents = status.activeIncidents;
    this.recentlyResolvedIncidents = status.recentlyResolvedIncidents;
    this.scheduledMaintenance = status.scheduledMaintenance;
    // Whether a window has finished is decided server-side, so the page never has to reason about
    // the visitor's clock or time zone.
    this.pastMaintenance = status.pastMaintenance ?? [];

    const historyByServiceKey = new Map((this.history?.series ?? []).map(s => [s.serviceKey, s.days]));
    const previouslyExpanded = new Map(this.components.map(c => [c.key, c.expanded]));

    this.components = GROUPS.map(group => {
      const backing = status.components.find(c => c.key === group.key);
      const serviceKeys = backing?.services.map(s => s.serviceKey) ?? group.fallbackServiceKeys;

      const serviceRows: ServiceRowView[] = serviceKeys.map(serviceKey => {
        const meta = SERVICE_META[serviceKey];
        const svc = backing?.services.find(s => s.serviceKey === serviceKey);
        return {
          key: serviceKey,
          icon: meta?.icon ?? 'ellipse-outline',
          labelKey: meta?.labelKey ?? serviceKey,
          state: svc?.state ?? 'Unknown',
          stale: svc?.stale ?? true,
          lastCheckedUtc: svc?.lastCheckedUtc ?? null,
          uptimePercent90d: svc?.uptimePercent90d ?? null,
          role: svc?.role ?? null,
          monitored: svc?.monitored ?? true,
          checkedAgo: checkedAgo(svc?.lastCheckedUtc ?? null),
          historyDays: combineHistory([serviceKey], historyByServiceKey),
          visibleDays: [],
          windowUptimePercent: svc?.uptimePercent90d ?? null,
          monitoredSince: null,
        };
      });

      return {
        key: group.key,
        icon: group.icon,
        iconSrc: group.iconSrc,
        labelKey: group.labelKey,
        state: backing?.state ?? 'Unknown',
        lastCheckedUtc: backing?.lastCheckedUtc ?? null,
        stale: backing?.stale ?? true,
        uptimePercent90d: backing?.uptimePercent90d ?? null,
        services: backing?.services ?? [],
        historyDays: cardHistory(group.key, serviceKeys, this.history?.groupSeries, historyByServiceKey),
        visibleDays: [],
        windowUptimePercent: backing?.uptimePercent90d ?? null,
        monitoredSince: null,
        serviceRows,
        serviceGroups: this.groupByRole(serviceRows),
        incidents: status.activeIncidents.filter(i => i.affectedGroups.includes(group.key)),
        expanded: previouslyExpanded.get(group.key) ?? true,
      };
    });

    this.applyDayWindow();
    this.cdr.markForCheck();
  }

  /**
   * Rows under one heading per role, core first. An API without roles keeps one unlabelled list, so the
   * card looks as before instead of inventing a weight the data does not carry.
   */
  private groupByRole(rows: ServiceRowView[]): ServiceGroupView[] {
    if (rows.every(r => r.role === null)) return [{ role: null, rows }];

    return ROLE_ORDER
      .map(role => ({ role, rows: rows.filter(r => r.role === role) }))
      .filter(group => group.rows.length > 0);
  }

  /** Cuts every bar down to the newest `visibleDayCount` days and re-derives the matching uptime. */
  private applyDayWindow(): void {
    const take = this.visibleDayCount;
    for (const component of this.components) {
      cutWindow(component, take);
      for (const row of component.serviceRows) cutWindow(row, take);
    }
  }
}
